/**
 * Leave-out rules keyed by inventory item — stored on default cleaning checklist staging.
 * Slice 44b: silent instant save; placements from Rooms tab.
 */
import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useChecklists, useCreateChecklist, useUpdateChecklist } from "@/hooks/useChecklists";
import {
  effectiveLeaveOutRule,
  newGuestAmenityStagingLine,
  parseGuestAmenityStagingFromChecklist,
} from "@/lib/guestAmenityStaging";
import type { GuestAmenityPlacement, LeaveOutRule } from "@/lib/leaveOut";
import { planHasLeaveOut, totalForRule, type LeaveOutPlan } from "@/lib/leaveOutPlan";
import type { GuestAmenityStagingLine } from "@/types/checklist";
import type { Room } from "@/types/checklist";
import type { Json } from "@/integrations/supabase/types";
import { toast } from "@/hooks/use-toast";

export function useInventoryLeaveOutRules(propertyId: string | undefined) {
  const queryClient = useQueryClient();
  const { data: checklists = [], isLoading } = useChecklists(propertyId);
  const updateChecklist = useUpdateChecklist();
  const createChecklist = useCreateChecklist();

  const targetChecklist = useMemo(() => {
    const cleaning = checklists.filter((c) => c.type === "cleaning" || !c.type);
    return cleaning.find((c) => c.is_default) ?? cleaning[0] ?? null;
  }, [checklists]);

  const lines = useMemo(
    () => parseGuestAmenityStagingFromChecklist(targetChecklist?.guest_amenity_staging),
    [targetChecklist?.guest_amenity_staging],
  );

  const ruleByItemId = useMemo(() => {
    const m = new Map<string, { rule: LeaveOutRule; line: GuestAmenityStagingLine }>();
    for (const line of lines) {
      if (!line.inventory_item_id) continue;
      m.set(line.inventory_item_id, { rule: effectiveLeaveOutRule(line), line });
    }
    return m;
  }, [lines]);

  const persistLines = useCallback(
    async (next: GuestAmenityStagingLine[]) => {
      if (!propertyId) return false;
      let checklist = targetChecklist;
      if (!checklist) {
        try {
          checklist = await createChecklist.mutateAsync({
            propertyId,
            name: "Turnover",
            type: "cleaning",
            rooms: [] as Room[],
            isDefault: true,
          });
        } catch (e) {
          toast({
            title: "Couldn’t create checklist",
            description: e instanceof Error ? e.message : undefined,
            variant: "destructive",
          });
          return false;
        }
      }
      if (!checklist?.id) return false;
      try {
        const rooms = (Array.isArray(checklist.rooms) ? checklist.rooms : []) as Room[];
        await updateChecklist.mutateAsync({
          id: checklist.id,
          propertyId,
          name: checklist.name,
          rooms,
          guest_amenity_staging: JSON.parse(JSON.stringify(next)) as Json,
        });
        await queryClient.invalidateQueries({
          queryKey: ["checklists-leave-out-staging", propertyId],
        });
        return true;
      } catch (e) {
        toast({
          title: "Couldn’t save leave-out",
          description: e instanceof Error ? e.message : undefined,
          variant: "destructive",
        });
        return false;
      }
    },
    [propertyId, targetChecklist, createChecklist, updateChecklist, queryClient],
  );

  const upsertRule = useCallback(
    async (
      itemId: string,
      itemName: string,
      rule: LeaveOutRule,
      placements: GuestAmenityPlacement[] = [],
    ) => {
      const cleaned: LeaveOutRule = {
        mode: rule.mode,
        qty: Math.max(1, Math.floor(rule.qty)),
        per_guests:
          rule.mode === "per_guests"
            ? Math.max(1, Math.floor(rule.per_guests ?? 1))
            : Math.max(1, Math.floor(rule.per_guests ?? 1)),
        min_qty: null,
        max_qty: null,
      };
      const existing = lines.find((l) => l.inventory_item_id === itemId);
      const firstRoom = placements[0];
      const nextLine: GuestAmenityStagingLine = existing
        ? {
            ...existing,
            item_label: itemName.trim() || existing.item_label,
            inventory_item_id: itemId,
            leave_out_rule: cleaned,
            target_count: cleaned.qty,
            placements: placements.length ? placements : [],
            property_room_id: firstRoom?.property_room_id ?? null,
            room_name_snapshot: firstRoom?.place_label ?? null,
          }
        : {
            ...newGuestAmenityStagingLine(),
            item_label: itemName.trim() || "Item",
            inventory_item_id: itemId,
            leave_out_rule: cleaned,
            target_count: cleaned.qty,
            placements: placements.length ? placements : [],
            property_room_id: firstRoom?.property_room_id ?? null,
            room_name_snapshot: firstRoom?.place_label ?? null,
          };
      const next = [
        ...lines.filter((l) => l.inventory_item_id !== itemId && l.id !== nextLine.id),
        nextLine,
      ];
      return persistLines(next);
    },
    [lines, persistLines],
  );

  /** Save several rules in ONE write (one-by-one upserts would each start from the same stale lines). */
  const upsertRules = useCallback(
    async (
      batch: { itemId: string; itemName: string; rule: LeaveOutRule; placements?: GuestAmenityPlacement[] }[],
    ) => {
      if (batch.length === 0) return true;
      const ids = new Set(batch.map((b) => b.itemId));
      const kept = lines.filter((l) => !l.inventory_item_id || !ids.has(l.inventory_item_id));
      const added = batch.map(({ itemId, itemName, rule, placements = [] }) => {
        const cleaned: LeaveOutRule = {
          mode: rule.mode,
          qty: Math.max(1, Math.floor(rule.qty)),
          per_guests: Math.max(1, Math.floor(rule.per_guests ?? 1)),
          min_qty: null,
          max_qty: null,
        };
        const first = placements[0];
        return {
          ...newGuestAmenityStagingLine(),
          item_label: itemName.trim() || "Item",
          inventory_item_id: itemId,
          leave_out_rule: cleaned,
          target_count: cleaned.qty,
          placements,
          property_room_id: first?.property_room_id ?? null,
          room_name_snapshot: first?.place_label ?? null,
        } as GuestAmenityStagingLine;
      });
      return persistLines([...kept, ...added]);
    },
    [lines, persistLines],
  );

  /** Save several leave-out plans in ONE write. */
  const upsertPlans = useCallback(
    async (batch: { itemId: string; itemName: string; plan: LeaveOutPlan }[]) => {
      const usable = batch.filter((b) => planHasLeaveOut(b.plan));
      if (usable.length === 0) return true;
      const ids = new Set(usable.map((b) => b.itemId));
      const kept = lines.filter((l) => !l.inventory_item_id || !ids.has(l.inventory_item_id));
      const added = usable.map(({ itemId, itemName, plan }) => {
        const every = plan.rules.find((r) => r.guests == null);
        const party = plan.rules
          .filter((r) => r.guests != null)
          .slice()
          .sort((a, b) => (a.guests ?? 0) - (b.guests ?? 0));
        const mirror = every && totalForRule(every) > 0 ? every : party.find((r) => totalForRule(r) > 0);
        const qty = mirror ? Math.max(1, totalForRule(mirror)) : 1;
        const firstPlace = mirror?.spots.find((s) => s.place.trim())?.place.trim() ?? null;
        return {
          ...newGuestAmenityStagingLine(),
          item_label: itemName.trim() || "Item",
          inventory_item_id: itemId,
          leave_out_rule: { mode: "fixed", qty, per_guests: 1, min_qty: null, max_qty: null } as LeaveOutRule,
          leave_out_plan: plan,
          target_count: qty,
          room_name_snapshot: firstPlace,
        } as GuestAmenityStagingLine;
      });
      return persistLines([...kept, ...added]);
    },
    [lines, persistLines],
  );

  const savePlan = useCallback(
    async (itemId: string, itemName: string, plan: LeaveOutPlan) => {
      if (!planHasLeaveOut(plan)) {
        const next = lines.filter((l) => l.inventory_item_id !== itemId);
        return persistLines(next);
      }
      const party = plan.rules
        .filter((r) => r.guests != null)
        .slice()
        .sort((a, b) => (a.guests ?? 0) - (b.guests ?? 0));
      const every = plan.rules.find((r) => r.guests == null);
      const mirror = every && totalForRule(every) > 0 ? every : party.find((r) => totalForRule(r) > 0);
      const qty = mirror ? Math.max(1, totalForRule(mirror)) : 1;
      const cleaned: LeaveOutRule = {
        mode: "fixed",
        qty,
        per_guests: 1,
        min_qty: null,
        max_qty: null,
      };
      const firstPlace = mirror?.spots.find((s) => s.place.trim())?.place.trim() ?? null;
      const existing = lines.find((l) => l.inventory_item_id === itemId);
      const nextLine: GuestAmenityStagingLine = existing
        ? {
            ...existing,
            item_label: itemName.trim() || existing.item_label,
            inventory_item_id: itemId,
            leave_out_rule: cleaned,
            leave_out_plan: plan,
            target_count: qty,
            room_name_snapshot: firstPlace ?? existing.room_name_snapshot ?? null,
          }
        : {
            ...newGuestAmenityStagingLine(),
            item_label: itemName.trim() || "Item",
            inventory_item_id: itemId,
            leave_out_rule: cleaned,
            leave_out_plan: plan,
            target_count: qty,
            room_name_snapshot: firstPlace,
          };
      const next = [
        ...lines.filter((l) => l.inventory_item_id !== itemId && l.id !== nextLine.id),
        nextLine,
      ];
      return persistLines(next);
    },
    [lines, persistLines],
  );

  const clearRule = useCallback(
    async (itemId: string) => {
      const next = lines.filter((l) => l.inventory_item_id !== itemId);
      return persistLines(next);
    },
    [lines, persistLines],
  );

  return {
    isLoading,
    ruleByItemId,
    upsertRule,
    upsertRules,
    upsertPlans,
    savePlan,
    clearRule,
    isSaving: updateChecklist.isPending || createChecklist.isPending,
  };
}
